import { useState } from "react"
import { MARK, MARK_COLORS, MARK_COLORS_DARK, MARK_VIEW_BOX } from "./logo-mark"

// The first-load screen, rendered before Tailwind arrives: inline styles, the mark from
// logo-mark.ts, and the two --background values repeated. See llms/app-boot.md.

// Timed from the head script's stamp, so each step lands once however often this remounts.
// Nothing shows before 2.3s: a usual load is over by then, and a flash of the mark is noise.
const elapsedSinceFirstPaint = () => {
  const start = Number(document.documentElement.dataset.bootStart)
  return Number.isFinite(start) ? Math.max(0, performance.now() - start) : 0
}

const styles = `
.boot {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: oklch(1 0 0);
  color: oklch(0.554 0.046 257.417);
  font-family: ui-sans-serif, system-ui, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  text-align: center;
}
.dark .boot {
  background: oklch(0.129 0.042 264.695);
  color: oklch(0.704 0.04 256.788);
}
.boot-mark {
  width: 40px;
  height: 40px;
  opacity: 0;
  animation:
    boot-in 400ms ease-out both,
    boot-pulse 1800ms ease-in-out infinite;
  animation-delay:
    calc(2.3s + var(--boot-elapsed)),
    calc(2.7s + var(--boot-elapsed));
}
.dark .boot-mark-from {
  stop-color: ${MARK_COLORS_DARK.from};
}
.dark .boot-mark-to {
  stop-color: ${MARK_COLORS_DARK.to};
}
.boot-messages {
  position: absolute;
  top: calc(50% + 44px);
  left: 50%;
  transform: translateX(-50%);
  display: grid;
  width: calc(100% - 48px);
  max-width: 32rem;
}
.boot-message {
  grid-area: 1 / 1;
  margin: 0;
  opacity: 0;
}
.boot-message-first {
  animation:
    boot-in 400ms ease-out both,
    boot-out 400ms ease-out forwards;
  animation-delay:
    calc(4s + var(--boot-elapsed)),
    calc(10s + var(--boot-elapsed));
}
.boot-message-slow {
  animation: boot-in 400ms ease-out both;
  animation-delay: calc(10.4s + var(--boot-elapsed));
}
.boot-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
@keyframes boot-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes boot-out {
  from { opacity: 1; }
  to { opacity: 0; }
}
@keyframes boot-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
@media (prefers-reduced-motion: reduce) {
  .boot-mark {
    animation: boot-in 1ms linear both;
    animation-delay: calc(2.3s + var(--boot-elapsed));
  }
  .boot-message-first {
    animation:
      boot-in 1ms linear both,
      boot-out 1ms linear forwards;
    animation-delay:
      calc(4s + var(--boot-elapsed)),
      calc(10s + var(--boot-elapsed));
  }
  .boot-message-slow {
    animation: boot-in 1ms linear both;
    animation-delay: calc(10.4s + var(--boot-elapsed));
  }
}
`

export const BootScreen = ({ message = "Loading the dashboard…" }: { message?: string }) => {
  // Read once per mount: a negative delay starts each animation part-way through, so
  // a remount picks up exactly where the previous instance left off.
  const [elapsed] = useState(elapsedSinceFirstPaint)

  return (
    <div className="boot" style={{ "--boot-elapsed": `${-elapsed}ms` } as React.CSSProperties}>
      <style>{styles}</style>
      <svg
        className="boot-mark"
        viewBox={MARK_VIEW_BOX}
        fill="none"
        role="img"
        aria-label="Cascade"
      >
        <defs>
          <linearGradient
            id="boot-mark-gradient"
            x1={MARK.gradient.x1}
            y1={MARK.gradient.y1}
            x2={MARK.gradient.x2}
            y2={MARK.gradient.y2}
            gradientUnits="userSpaceOnUse"
          >
            <stop className="boot-mark-from" stopColor={MARK_COLORS.from} />
            <stop className="boot-mark-to" offset="1" stopColor={MARK_COLORS.to} />
          </linearGradient>
        </defs>
        <g transform={MARK.transform} fill="url(#boot-mark-gradient)">
          {MARK.streams.map((d) => (
            <path key={d} d={d} />
          ))}
          <path
            d={MARK.star}
            stroke="url(#boot-mark-gradient)"
            strokeWidth={MARK.starStroke}
            strokeLinejoin="round"
          />
        </g>
      </svg>
      {/* One steady line for assistive tech: the visible pair is timed with CSS, which
          leaves both of them in the accessibility tree at once. */}
      <p className="boot-sr">{message}</p>
      <div className="boot-messages" aria-hidden="true">
        <p className="boot-message boot-message-first">{message}</p>
        <p className="boot-message boot-message-slow">
          This is taking longer than it should. Try refreshing the page, and get in touch if it
          keeps happening.
        </p>
      </div>
    </div>
  )
}
